import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { FleetSummary } from '../types';
import { fetchDriverFleet } from '../services/driverWorkspaceService';

const Fleet: React.FC = () => {
  const [fleet, setFleet] = useState<FleetSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDriverFleet()
      .then(setFleet)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  if (!fleet) {
    return <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>Fleet details are not available.</Paper>;
  }

  const seatPercent = Math.min((fleet.usedSeats / fleet.teamSeats) * 100, 100);

  return (
    <Stack spacing={3} sx={{ maxWidth: 1080, mx: 'auto' }}>
      {fleet.upgradePrompt ? (
        <Paper
          elevation={0}
          sx={{ p: { xs: 2.5, md: 3 }, bgcolor: '#061b31', color: 'white', background: 'linear-gradient(135deg,#061b31,#0b3156)' }}
        >
          <Typography variant="h4" fontWeight={900}>Fleet workspace</Typography>
          <Typography color="rgba(255,255,255,0.72)" mt={0.75}>
            Fleet seats and shared credits are available through support for larger operators.
          </Typography>
          <Button component={RouterLink} to="/support" variant="contained" color="secondary" sx={{ mt: 2 }}>
            Contact support
          </Button>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="h5" fontWeight={900}>Fleet workspace</Typography>
          <Typography variant="body2" color="text.secondary">Manage shared credits and team driver performance.</Typography>
        </Paper>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Seats used</Typography>
            <Typography variant="h4" fontWeight={900}>{fleet.usedSeats}/{fleet.teamSeats}</Typography>
            <LinearProgress variant="determinate" value={seatPercent} sx={{ mt: 1.5, height: 8, borderRadius: 4 }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Shared credits</Typography>
            <Typography variant="h4" fontWeight={900}>
              {fleet.sharedCredits.monthlyLeadLimit === null ? 'Unlimited' : fleet.sharedCredits.remaining ?? 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Fleet status</Typography>
            <Typography variant="h4" fontWeight={900}>{fleet.enabled ? 'Active' : 'Locked'}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
          <Typography variant="h6" fontWeight={900}>Team members</Typography>
          <Button disabled={!fleet.enabled} variant="outlined">Invite driver</Button>
        </Box>
        <Stack spacing={1.5}>
          {fleet.members.map((member) => (
            <Paper key={member.id} variant="outlined" sx={{ p: 1.75, bgcolor: '#fbfcff' }}>
              <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box>
                  <Typography fontWeight={900}>{member.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{member.email} · {member.phone || 'No phone'}</Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={member.role} size="small" />
                  <Chip label={member.isOnline ? 'Online' : 'Offline'} color={member.isOnline ? 'success' : 'default'} size="small" />
                  <Chip label={`${member.unlocksUsed} unlocks`} variant="outlined" size="small" />
                  <Chip label={`${member.ridesWon} wins`} variant="outlined" size="small" />
                </Stack>
              </Box>
            </Paper>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default Fleet;
